import React, { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import Loading from "react-loading";

import { SidebarContext } from "../../AppRoutes";
import { getPedidosFinalizadosPeriodo, getClientesAtivos } from "../../services/api";
import { nowTz, formatDateTz } from '../../utils/timezone'
import { getStoredUser } from '../../utils/safeStorage';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IoStatsChartOutline } from 'react-icons/io5';
import { formatPriceWithSymbol } from '../../utils/currency';

import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';
import money from '../../assets/svg/money.svg';
import blogging from '../../assets/svg/blogging.svg';
import graphicalPresentation from '../../assets/svg/graphical-presentation.svg';
import employeeShift from '../../assets/svg/employee-shift.svg';

const centerTextPlugin = {
    id: 'centerTextPlugin',
    beforeDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();

        const quantidade = chart.data.datasets[0]?.data?.[0];
        const faturamento = chart.data.datasets[0]?.faturamento;

        if (faturamento === undefined) {
            ctx.restore();
            return;
        }

        const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(quantidade ?? 0, centerX, centerY + 40);

        ctx.font = 'bold 14px Arial';
        ctx.textBaseline = 'top';
        ctx.fillText(formatPriceWithSymbol(faturamento ?? 0), centerX, centerY + 50);

        ctx.restore();
    }
};

ChartJS.register(ArcElement, Tooltip, Legend, centerTextPlugin);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;
        min-height: 40px;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        .filters {
            width: 100%;
            gap: 8px;
        }

        input[type="date"] {
            flex: 1;
            min-width: 0;
            padding: 8px 10px;
            font-size: 13px;
        }

        button {
            flex: 1;
            padding: 8px 12px;
            font-size: 13px;
        }
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    flex-direction: column;
    gap: 16px;

    span {
        color: #666;
        font-size: 14px;
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
        margin-bottom: 20px;
    }

    @media (max-width: 360px) {
        grid-template-columns: 1fr;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;

        .icon {
            width: 32px;
            height: 32px;
            font-size: 16px;
            margin-bottom: 4px;
        }

        .label { font-size: 12px; }
        .value { font-size: 22px; }
        .sub { font-size: 11px; }
    }
`;

const PerformanceCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    padding: 24px;
    margin-bottom: 30px;
`;

const PeriodLabel = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 8px;

    span {
        font-size: 14px;
        color: #666;
        font-weight: 500;
    }
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 28px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

const MetricItem = styled.div`
    text-align: center;
    padding: 16px;
    border-radius: 12px;
    background: #f8f9fa;

    span {
        display: block;
        color: #666;
        font-size: 14px;
        margin-bottom: 4px;
    }

    strong {
        display: block;
        font-size: 24px;
        font-weight: 700;
        color: #333;
        margin-bottom: 4px;
    }

    small {
        font-size: 12px;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        padding: 12px;

        strong {
            font-size: 20px;
        }
    }
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const ChartItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 16px 8px;
    border-radius: 12px;
    background: #f8f9fa;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .chart-wrapper {
        width: 100%;
        max-width: 180px;
        position: relative;
    }

    .chart-label {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        --margin-top: -40px;
        position: relative;
        z-index: 1;
    }

    @media (max-width: 500px) {
        padding: 12px 6px;

        .chart-wrapper {
            max-width: 140px;
        }

        .chart-label {
            font-size: 12px;
            --margin-top: -30px;
        }
    }
`;

const RelatorioGeral = () => {

    const userParse = getStoredUser();
    const userID = userParse?._id;

    const empresa = localStorage.getItem('empresa')
    const empresaParse = JSON.parse(empresa)
    const idEmpresa = empresaParse.id_empresa;
    const vinculo_empresa = empresaParse.cnpj;

    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [pedidosFinalizadosNoPeriodo, setPedidosFinalizadosNoPeriodo] = useState([]);

    const [faturamentoTotalPedidosFinalizados, setFaturamentoTotalPedidosFinalizados] = useState(0);
    const [ticketMedioPedidos, setTicketMedioPedidos] = useState(0);
    const [percentualFaturamento, setPercentualFaturamento] = useState(0);
    const [percentualPedidos, setPercentualPedidos] = useState(0);
    const [percentualTicketMedio, setPercentualTicketMedio] = useState(0);
    const [clientesAtivosLength, setClientesAtivosLength] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    // Dados para os gráficos de pedidos por modalidade
    const [data, setData] = useState({
        entregas: { quantidade: 0, faturamento: 0 },
        retiradas: { quantidade: 0, faturamento: 0 },
        mesa: { quantidade: 0, faturamento: 0 },
        taxa_entrega: { quantidade: 0, faturamento: 0 }
    });

    const commonOptions = {
        rotation: -90,
        circumference: 180,
        cutout: '80%',
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true
            }
        },
        animation: {
            animateRotate: true
        },
        elements: {
            arc: {
                borderWidth: 0
            }
        }
    };

    const optionsEntregaRetirada = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            centerTextPlugin
        }
    };

    const fetchPedidosFinalizados = useCallback(async (startDateSend, endDateSend) => {
        setIsLoading(true);
        try {
            const startDateObj = typeof startDateSend === 'string' ? new Date(startDateSend + 'T00:00:00') : startDateSend;
            const endDateObj = typeof endDateSend === 'string' ? new Date(endDateSend + 'T23:59:59') : endDateSend;

            const response = await getPedidosFinalizadosPeriodo(
                userID, idEmpresa, vinculo_empresa, '4', startDateObj, endDateObj
            );

            const clientesAtivos = await getClientesAtivos(startDateObj, endDateObj);
            if (clientesAtivos.data.estatisticas?.totalClientes > 0) {
                setClientesAtivosLength(clientesAtivos.data.estatisticas.totalClientes);
            }

            if (response.data.pedidos) {
                const pedidos = response.data.pedidos;
                const pedidosComparativos = response.data.pedidosComparativos || [];

                setPedidosFinalizadosNoPeriodo(pedidos);

                let faturamentoTotalAtual = 0;
                pedidos.forEach(pedido => {
                    if (pedido.valor_total !== undefined) {
                        faturamentoTotalAtual += pedido.valor_total;
                    }
                });

                const entregues = pedidos.filter(pedido => pedido.entrega?.tipo_entrega?.toLowerCase() === 'entrega');
                const retirados = pedidos.filter(pedido => pedido.entrega?.tipo_entrega?.toLowerCase() === 'retirada');
                const mesa = pedidos.filter(pedido => pedido.entrega?.tipo_entrega?.toLowerCase() === 'mesa' || pedido.origem_pedido === 'mesa');

                const faturamentoEntregues = entregues.reduce((total, pedido) => total + (pedido.valor_total || 0), 0);
                const faturamentoRetirados = retirados.reduce((total, pedido) => total + (pedido.valor_total || 0), 0);
                const faturamentoMesa = mesa.reduce((total, pedido) => total + (pedido.valor_total || 0), 0);
                const faturamentoTaxa_entrega = entregues.reduce((total, pedido) => total + (pedido.entrega?.valor || 0), 0);

                setData({
                    entregas: {
                        quantidade: entregues.length,
                        faturamento: faturamentoEntregues
                    },
                    retiradas: {
                        quantidade: retirados.length,
                        faturamento: faturamentoRetirados
                    },
                    mesa: {
                        quantidade: mesa.length,
                        faturamento: faturamentoMesa
                    },
                    taxa_entrega: {
                        quantidade: entregues.length,
                        faturamento: faturamentoTaxa_entrega
                    }
                });

                const ticketMedioAtual = pedidos.length ? (faturamentoTotalAtual / pedidos.length) : 0;

                setFaturamentoTotalPedidosFinalizados(faturamentoTotalAtual);
                setTicketMedioPedidos(ticketMedioAtual);

                let faturamentoTotalComparativo = 0;
                pedidosComparativos.forEach(pedido => {
                    if (pedido.valor_total !== undefined) {
                        faturamentoTotalComparativo += pedido.valor_total;
                    }
                });

                const ticketMedioComparativo = pedidosComparativos.length ? (faturamentoTotalComparativo / pedidosComparativos.length) : 0;

                const percentualFaturamento = faturamentoTotalComparativo ? ((faturamentoTotalAtual - faturamentoTotalComparativo) / faturamentoTotalComparativo) * 100 : 100;
                const percentualPedidos = pedidosComparativos.length ? ((pedidos.length - pedidosComparativos.length) / pedidosComparativos.length) * 100 : 100;
                const percentualTicketMedio = ticketMedioComparativo ? ((ticketMedioAtual - ticketMedioComparativo) / ticketMedioComparativo) * 100 : 100;

                setPercentualFaturamento(percentualFaturamento);
                setPercentualPedidos(percentualPedidos);
                setPercentualTicketMedio(percentualTicketMedio);
            }
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao buscar pedidos finalizados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [userID, idEmpresa, vinculo_empresa, empresaParse._id]);

    useEffect(() => {
        fetchPedidosFinalizados(startDate, endDate);
    }, [startDate, endDate]);

    const handleAtualizar = () => {
        fetchPedidosFinalizados(startDate, endDate);
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><IoStatsChartOutline /></div>
                            <h1 className="page-title">{t('general.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={handleAtualizar} disabled={isLoading}>
                                {isLoading ? t('common.updating') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={50} width={50} />
                            <span>{t('common.loadingData')}</span>
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <img src={money} height={40} width={40} alt="" />
                                    <span className="label">{t('common.revenue')}</span>
                                    <span className="value">{formatPriceWithSymbol(faturamentoTotalPedidosFinalizados)}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>
                                <MetricCard>
                                    <img src={graphicalPresentation} height={40} width={40} alt="" />
                                    <span className="label">{t('common.avgTicket')}</span>
                                    <span className="value">{formatPriceWithSymbol(ticketMedioPedidos)}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>
                                <MetricCard>
                                    <img src={blogging} height={40} width={40} alt="" />
                                    <span className="label">{t('general.totalOrders')}</span>
                                    <span className="value">{pedidosFinalizadosNoPeriodo.length}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>
                                <MetricCard>
                                    <img src={employeeShift} height={40} width={40} alt="" />
                                    <span className="label">{t('general.activeClients')}</span>
                                    <span className="value">{clientesAtivosLength || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>
                            </CardsGrid>

                            <PerformanceCard>
                                <PeriodLabel>
                                    <span>{t('common.periodRange', { start: formatDateTz(startDate, 'DD/MM/YYYY'), end: formatDateTz(endDate, 'DD/MM/YYYY') })}</span>
                                </PeriodLabel>

                                <MetricsRow>
                                    <MetricItem>
                                        <span>{t('common.revenue')}</span>
                                        <strong>{formatPriceWithSymbol(faturamentoTotalPedidosFinalizados)}</strong>
                                        <small style={{ color: percentualFaturamento >= 0 ? '#28a745' : '#dc3545' }}>
                                            {percentualFaturamento >= 0 ? `▲ ${percentualFaturamento.toFixed(2)}%` : `▼ ${percentualFaturamento.toFixed(2)}%`}
                                        </small>
                                    </MetricItem>
                                    <MetricItem>
                                        <span>{t('common.orders')}</span>
                                        <strong>{pedidosFinalizadosNoPeriodo.length}</strong>
                                        <small style={{ color: percentualPedidos >= 0 ? '#28a745' : '#dc3545' }}>
                                            {percentualPedidos >= 0 ? `▲ ${percentualPedidos.toFixed(2)}%` : `▼ ${percentualPedidos.toFixed(2)}%`}
                                        </small>
                                    </MetricItem>
                                    <MetricItem>
                                        <span>{t('common.avgTicket')}</span>
                                        <strong>{formatPriceWithSymbol(ticketMedioPedidos)}</strong>
                                        <small style={{ color: percentualTicketMedio >= 0 ? '#28a745' : '#dc3545' }}>
                                            {percentualTicketMedio >= 0 ? `▲ ${percentualTicketMedio.toFixed(2)}%` : `▼ ${percentualTicketMedio.toFixed(2)}%`}
                                        </small>
                                    </MetricItem>
                                </MetricsRow>

                                <ChartsGrid>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.entregas.quantidade, pedidosFinalizadosNoPeriodo.length - data.entregas.quantidade],
                                                    backgroundColor: ['#007bff', '#e9ecef'],
                                                    faturamento: data.entregas.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.delivery')}</span>
                                    </ChartItem>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.retiradas.quantidade, pedidosFinalizadosNoPeriodo.length - data.retiradas.quantidade],
                                                    backgroundColor: ['#28a745', '#e9ecef'],
                                                    faturamento: data.retiradas.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.pickup')}</span>
                                    </ChartItem>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.mesa.quantidade, pedidosFinalizadosNoPeriodo.length - data.mesa.quantidade],
                                                    backgroundColor: ['#ff9800', '#e9ecef'],
                                                    faturamento: data.mesa.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.table')}</span>
                                    </ChartItem>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.taxa_entrega.quantidade, pedidosFinalizadosNoPeriodo.length - data.taxa_entrega.quantidade],
                                                    backgroundColor: ['#dc3545', '#e9ecef'],
                                                    faturamento: data.taxa_entrega.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.deliveryFee')}</span>
                                    </ChartItem>
                                </ChartsGrid>
                            </PerformanceCard>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    )
}

export default RelatorioGeral;
